import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Stack, TextField } from '@mui/material';

import {
  ConfigResponse,
  PartialPaymentValue,
  TippingPercentagesValue,
} from '../../../store/modules/merchant/types';
import { ConfigSection, ConfigSwitchSection, ParentAwareSaveBar } from '../../../common';
import { InheritedConfigSettingCallouts } from '../../../common/InheritedConfigSettingCallouts';
import { useChildInheritedConfigEdit } from '../../../hooks/useChildInheritedConfigEdit';
import { isConfigInheritedForMerchant } from '../../../util/configInheritance';
import { inheritableLabelsFromConfigNames } from '../../../util/parentSettingSave';
import { useMerchant } from '../../../contexts/MerchantContext';
import { Alert } from '../../Paylinks/components/Alert';
import { BlockedByPartialPaymentsMessage } from '../../../common/PartialPaymentsConflictAlertMessage';
import { getFeeOrDiscountToggleDisabledReasonForOwnedPartialPayments } from '../../../util/partialPaymentConfig';

/** Matches the tipping percentages field row below the switch. */
const TIPPING_FORM_CONTENT_MAX_WIDTH = '612px';

// backend restricts percentages to integers between 1 and 100
const onPercentChange = (
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  setLocalTipPreset: Dispatch<SetStateAction<string>>,
) => {
  if (event.target.value !== '0' && event.target.value.match(/^[0-9]*$/)) {
    const newValue = event.target.value;
    const intNewValue = parseInt(event.target.value, 10);
    if (!!newValue && (intNewValue < 1 || intNewValue > 100)) {
      return;
    }
    setLocalTipPreset(newValue);
  }
};

type Props = {
  tippingPercentagesConfig?: ConfigResponse<TippingPercentagesValue>;
  partialPaymentsConfig?: ConfigResponse<PartialPaymentValue>;
  isTippingSupported: boolean;
  onSave: (config: { value: TippingPercentagesValue; enabled: boolean }) => Promise<void>;
  isSaving?: boolean;
  readOnly?: boolean;
};

export const TippingForm = ({
  isTippingSupported,
  tippingPercentagesConfig,
  partialPaymentsConfig,
  onSave,
  readOnly = false,
  isSaving = false,
}: Props) => {
  const { merchantId } = useMerchant();
  const settingInherited = isConfigInheritedForMerchant(merchantId, tippingPercentagesConfig);
  const {
    showInheritedCallouts,
    isCustomizingInherited,
    controlsDisabled,
    onCustomize,
    resetCustomizing,
    getSaveBarInheritanceProps,
    isChildInheritedSaveBarOpen,
    isChildInheritedSaveEnabled,
  } = useChildInheritedConfigEdit(settingInherited, readOnly);

  const [isTippingEnabled, tippingPercentages] = useMemo(
    () => [!!tippingPercentagesConfig?.enabled, tippingPercentagesConfig?.value || []],
    [tippingPercentagesConfig?.enabled, tippingPercentagesConfig?.value],
  );
  const [existingTipPreset1, existingTipPreset2, existingTipPreset3] = tippingPercentages;

  const [isLocalTippingEnabled, setIsLocalTippingEnabled] = useState(isTippingEnabled);
  const [localTipPreset1, setLocalTipPreset1] = useState(existingTipPreset1?.toString() ?? '');
  const [localTipPreset2, setLocalTipPreset2] = useState(existingTipPreset2?.toString() ?? '');
  const [localTipPreset3, setLocalTipPreset3] = useState(existingTipPreset3?.toString() ?? '');

  const hasErrorTip1 =
    !!localTipPreset1 &&
    (localTipPreset1 === localTipPreset2 || localTipPreset1 === localTipPreset3);
  const hasErrorTip2 =
    !!localTipPreset2 &&
    (localTipPreset2 === localTipPreset1 || localTipPreset2 === localTipPreset3);
  const hasErrorTip3 =
    !!localTipPreset3 &&
    (localTipPreset3 === localTipPreset1 || localTipPreset3 === localTipPreset2);

  useEffect(() => {
    resetCustomizing();
  }, [tippingPercentagesConfig?.configId, resetCustomizing]);

  const onCancel = useCallback(() => {
    setIsLocalTippingEnabled(isTippingEnabled);
    setLocalTipPreset1(existingTipPreset1?.toString() ?? '');
    setLocalTipPreset2(existingTipPreset2?.toString() ?? '');
    setLocalTipPreset3(existingTipPreset3?.toString() ?? '');
    resetCustomizing();
  }, [
    isTippingEnabled,
    existingTipPreset1,
    existingTipPreset2,
    existingTipPreset3,
    resetCustomizing,
  ]);

  const fieldsDisabled = !isTippingSupported || controlsDisabled;

  const tippingToggleDisabledReason = useMemo(
    () =>
      getFeeOrDiscountToggleDisabledReasonForOwnedPartialPayments(
        partialPaymentsConfig,
        merchantId,
        isLocalTippingEnabled,
        'TIPPING_PERCENTAGES',
      ),
    [partialPaymentsConfig, merchantId, isLocalTippingEnabled],
  );

  const isSaveBarOpen = useMemo(() => {
    const hasAnyPresetChanged = [localTipPreset1, localTipPreset2, localTipPreset3]
      .filter((preset) => !!preset)
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      .some((localPreset, index) => {
        const existingValue =
          tippingPercentages.length > index ? tippingPercentages[index]?.toString() : '';
        return localPreset !== existingValue;
      });
    return isLocalTippingEnabled !== isTippingEnabled || hasAnyPresetChanged;
  }, [
    isLocalTippingEnabled,
    isTippingEnabled,
    localTipPreset1,
    localTipPreset2,
    localTipPreset3,
    tippingPercentages,
  ]);

  const isFormDirty = isSaveBarOpen;
  const showSaveBar = isChildInheritedSaveBarOpen(isFormDirty);
  const pageInheritableLabels = useMemo(
    () => inheritableLabelsFromConfigNames(['TIPPING_PERCENTAGES']),
    [],
  );
  const saveBarInheritance = getSaveBarInheritanceProps(
    showSaveBar,
    isFormDirty ? pageInheritableLabels : [],
    pageInheritableLabels,
  );

  const handleSave = useCallback(async () => {
    const localTippingPercentages: number[] = [];
    if (localTipPreset1) {
      localTippingPercentages.push(parseInt(localTipPreset1, 10));
    }
    if (localTipPreset2) {
      localTippingPercentages.push(parseInt(localTipPreset2, 10));
    }
    if (localTipPreset3) {
      localTippingPercentages.push(parseInt(localTipPreset3, 10));
    }
    localTippingPercentages.sort((a, b) => a - b);

    await onSave({
      value: localTippingPercentages,
      enabled: isLocalTippingEnabled,
    });
    resetCustomizing();
  }, [
    isLocalTippingEnabled,
    localTipPreset1,
    localTipPreset2,
    localTipPreset3,
    onSave,
    resetCustomizing,
  ]);

  return (
    <>
      {showInheritedCallouts ? (
        <InheritedConfigSettingCallouts
          isCustomizingInherited={isCustomizingInherited}
          readOnly={readOnly}
          onCustomize={onCustomize}
        />
      ) : null}
      {!isTippingSupported && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your Ecommerce platform does not support tipping. Please reach out to support@skipify.com
          if you need assistance.
        </Alert>
      )}
      <Box sx={{ opacity: isTippingSupported ? 1 : 0.45 }}>
        <Stack spacing={1}>
          <ConfigSwitchSection
            title="Tipping"
            description="Enable/Disable Tipping"
            isChecked={isLocalTippingEnabled}
            onToggle={() => {
              setIsLocalTippingEnabled(!isLocalTippingEnabled);
            }}
            disabled={fieldsDisabled || !!tippingToggleDisabledReason}
            switchTestId="TIPPING_SWITCH"
          />
          {tippingToggleDisabledReason ? (
            <Alert
              severity="warning"
              size="small"
              sx={{ width: '100%', maxWidth: TIPPING_FORM_CONTENT_MAX_WIDTH }}
            >
              <BlockedByPartialPaymentsMessage blockedLabel="Tipping" />
            </Alert>
          ) : null}
        </Stack>
        <Box sx={{ mt: 4 }}>
          <ConfigSection
            title="Tipping Percentages"
            descriptionMaxWidth={TIPPING_FORM_CONTENT_MAX_WIDTH}
            description="When tipping is enabled, a Custom tip option will display to your customers during
          checkout. If you wish to provide preset tip options for your customers, you can add up to
          three percentages to display. Tip percentages can be whole numbers between 1 and 100."
          >
            <Stack
              spacing={2}
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ mt: 2, maxWidth: TIPPING_FORM_CONTENT_MAX_WIDTH }}
            >
              <TextField
                label="Percentage 1"
                id="percentage1"
                value={localTipPreset1 || ''}
                onChange={(event) => {
                  onPercentChange(event, setLocalTipPreset1);
                }}
                disabled={fieldsDisabled || !isLocalTippingEnabled}
                sx={localTipPreset1 ? { fieldset: { borderColor: 'primary.dark' } } : {}}
                error={hasErrorTip1}
                helperText={hasErrorTip1 && 'Each preset tip option must be unique'}
              />
              <TextField
                label="Percentage 2"
                id="percentage2"
                value={localTipPreset2 || ''}
                onChange={(event) => {
                  onPercentChange(event, setLocalTipPreset2);
                }}
                disabled={fieldsDisabled || !isLocalTippingEnabled}
                sx={localTipPreset2 ? { fieldset: { borderColor: 'primary.dark' } } : {}}
                error={hasErrorTip2}
                helperText={hasErrorTip2 && 'Each preset tip option must be unique'}
              />
              <TextField
                label="Percentage 3"
                id="percentage3"
                value={localTipPreset3 || ''}
                onChange={(event) => {
                  onPercentChange(event, setLocalTipPreset3);
                }}
                disabled={fieldsDisabled || !isLocalTippingEnabled}
                sx={localTipPreset3 ? { fieldset: { borderColor: 'primary.dark' } } : {}}
                error={hasErrorTip3}
                helperText={hasErrorTip3 && 'Each preset tip option must be unique'}
              />
              <TextField variant="outlined" label="Custom tip" id="custom" type="number" disabled />
            </Stack>
          </ConfigSection>
        </Box>
      </Box>
      <ParentAwareSaveBar
        show={showSaveBar && !readOnly}
        saveLoading={isSaving}
        onSave={handleSave}
        onCancel={onCancel}
        inheritableSettingLabels={saveBarInheritance.inheritableSettingLabels}
        detailTextOverride={saveBarInheritance.detailTextOverride}
        disableSave={
          isSaving ||
          hasErrorTip1 ||
          hasErrorTip2 ||
          hasErrorTip3 ||
          !isChildInheritedSaveEnabled(isFormDirty)
        }
      />
    </>
  );
};
